/**
 * @file Chiffres de degats et gains flottants (pool de 40 elements DOM, animation CSS) : apparition en "pop" (sans tremblement d'ecran),
 * critiques plus gros jaune/rouge avec un pop plus marque, billets verts +argent.
 */
import { RENDER } from '@config/index';
import { formatBig } from '@shared/format';
import { Component } from '../core/Component';
import { h } from '../core/dom';
import { motionReduced } from '../core/motion';
import type { UiContext } from '../UiContext';

/** Au plus MAX_SPAWNS apparitions par fenetre de WINDOW_MS (le reste est ignore : lisibilite + cout DOM). */
const MAX_SPAWNS = 12;
const WINDOW_MS = 100;
const JITTER_PX = 16;
const HEIGHT_DAMAGE = 1.2;
const HEIGHT_MONEY = 1.8;

/** Marge (px) gardee entre un chiffre et le bord de l'ecran : un arbre au bord (la foret est souvent en lisiere) affiche quand meme ses degats. */
const EDGE_MARGIN_X = 30;
const EDGE_MARGIN_Y = 22;
/** Au-dela de cette distance hors ecran (fraction de la taille), la cible n'est pas visible du tout : pas de chiffre. */
const OFFSCREEN_SLACK = 0.25;
/** Filet de securite : un chiffre dont l'animation n'a jamais signale sa fin (annulee, onglet en veille...) est recupere apres ce delai. */
const STALE_MS = 2500;

type Kind = 'hit' | 'crit' | 'money' | 'boss';

/**
 * Position d'affichage d'un chiffre : ramenee dans l'ecran (marges) si la cible est au bord ou legerement hors champ ; null si elle
 * est franchement hors de l'ecran. Pur (teste) : (x, y) en px CSS dans un conteneur de taille (w, h).
 */
export const placeInView = (x: number, y: number, w: number, h: number): { x: number; y: number } | null => {
  if (w <= 0 || h <= 0) return { x, y };
  if (x < -w * OFFSCREEN_SLACK || x > w * (1 + OFFSCREEN_SLACK) || y < -h * OFFSCREEN_SLACK || y > h * (1 + OFFSCREEN_SLACK)) return null;
  const mx = Math.min(EDGE_MARGIN_X, w / 2);
  const my = Math.min(EDGE_MARGIN_Y, h / 2);
  return { x: Math.min(w - mx, Math.max(mx, x)), y: Math.min(h - my, Math.max(my, y)) };
};

export class FloatingTexts extends Component {
  private readonly free: HTMLSpanElement[] = [];
  private flip = false;
  private windowStart = 0;
  private windowCount = 0;
  private readonly spawnedAt = new Map<HTMLSpanElement, number>();
  private viewW = 0;
  private viewH = 0;

  constructor(ctx: UiContext) {
    super(ctx, 'div', 'floaters');
  }

  protected build(): void {
    for (let i = 0; i < RENDER.fx.maxFloatingTexts; i++) {
      const span = h('span', { class: 'ft' });
      this.free.push(span);
      this.el.append(span);
      // La liberation se fait a la fin de l'animation CSS (le span reste dans le DOM, invisible).
      this.on(span, 'animationend', () => this.release(span));
      // animation annulee (conteneur masque, changement de classe...) : sans cela le span ne revenait jamais dans le pool
      this.on(span, 'animationcancel', () => this.release(span));
    }
    // Taille du conteneur mise en cache (lire clientWidth a chaque chiffre forcerait un recalcul de mise en page).
    const measure = (): void => {
      this.viewW = this.el.clientWidth;
      this.viewH = this.el.clientHeight;
    };
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(this.el);
      this.addDisposer(() => ro.disconnect());
    }
    requestAnimationFrame(measure);
    this.listen('EntityHit', (e) => {
      if (!this.ctx.sim.data.settings.damageNumbers) return;
      this.spawn(e.x, e.y, HEIGHT_DAMAGE, formatBig(e.damage), e.crit ? 'crit' : 'hit');
    });
    this.listen('TargetKilled', (e) => {
      if (!this.ctx.sim.data.settings.damageNumbers || e.money.lte(0)) return;
      this.spawn(e.x, e.y, HEIGHT_MONEY, `+${formatBig(e.money)}`, e.isBoss ? 'boss' : 'money');
    });
  }

  private release(span: HTMLSpanElement): void {
    if (!span.classList.contains('on')) return;
    span.classList.remove('on');
    span.style.animationName = '';
    this.spawnedAt.delete(span);
    this.free.push(span);
  }

  override update(_dt: number): void {
    if (this.spawnedAt.size === 0) return;
    const now = performance.now();
    for (const [span, t] of this.spawnedAt) if (now - t > STALE_MS) this.release(span);
  }

  private spawn(x: number, y: number, height: number, text: string, kind: Kind): void {
    const now = performance.now();
    if (now - this.windowStart > WINDOW_MS) {
      this.windowStart = now;
      this.windowCount = 0;
    }
    if (this.windowCount >= MAX_SPAWNS) return;
    const span = this.free.pop();
    if (!span) return;
    const proj = this.ctx.projector.worldToScreen(x, y, height);
    const jitter = (Math.random() - 0.5) * 2 * JITTER_PX;
    const p = proj.visible ? placeInView(proj.x + jitter, proj.y, this.viewW, this.viewH) : null;
    if (!p) {
      this.free.push(span);
      return;
    }
    this.windowCount++;
    this.spawnedAt.set(span, now);
    this.flip = !this.flip;
    span.textContent = text;
    span.className = `ft on ${kind}`;
    span.style.setProperty('--x', `${p.x.toFixed(1)}px`);
    span.style.setProperty('--y', `${p.y.toFixed(1)}px`);
    // Deux noms de keyframes identiques en alternance : relance l'animation sans forcer de reflow.
    // reduire les animations : simple fondu montant, sans pop ni rotation (les critiques restent distinguables par la couleur/taille)
    const base = motionReduced(this.ctx) ? 'float-fade' : kind === 'crit' ? 'float-crit' : 'float-up';
    span.style.animationName = this.flip ? base : `${base}-b`;
  }
}
